// Cohérence des règles d'un palier prop firm, AVANT enregistrement.
//
// POURQUOI. Huit paliers Lucid ont été saisis avec `drawdownType: "eod"` — ce
// qui pose `hasTrailingDrawdown: true` automatiquement — mais leur montant rangé
// dans « Max Total Loss » au lieu de « Trailing Drawdown ». Le formulaire
// affiche les deux champs côte à côte, et rien ne signalait la contradiction.
//
// Conséquence dans le moteur : `trailingDrawdownResolved` vaut null, la branche
// trailing est sautée, et `computeBufferStatus` retombe sur la règle STATIQUE
// `total_loss`. Le plancher se fige sur le solde initial au lieu de suivre le
// pic — un compte cramé n'est jamais détecté, et sa clôture est archivée
// « Challenge validé » (cf. lucidTrailingFloor.test.ts, 18 comptes touchés).
//
// L'échec était SILENCIEUX et FAVORABLE au trader : invisible jusqu'à ce qu'un
// compte ferme et qu'un ticket de support arrive. D'où cette validation à la
// saisie, qui transforme un bug de données en message d'erreur immédiat.
//
// Module PUR : aucun import Wasp/Prisma/React, testable isolément.

/** Sous-ensemble d'un palier nécessaire à la validation. */
export interface TierRuleInput {
  drawdownType: string | null;
  hasTrailingDrawdown: boolean;
  trailingDrawdownAmount: number | null;
  trailingDrawdownPercent: number | null;
  maxTotalLossAmount: number | null;
  maxTotalLossPercent: number | null;
}

export interface TierRuleProblem {
  /** Champ fautif, pour cibler l'input à surligner. */
  field: string;
  /** Message affiché à l'admin. */
  message: string;
}

/**
 * Ce type de drawdown ancre-t-il son plancher au PIC (trailing) ?
 *
 * SOURCE UNIQUE DE VÉRITÉ du vocabulaire, partagée avec `propFirmEngine.ts`.
 * Elle existe parce que les deux avaient DIVERGÉ : le moteur ne reconnaissait
 * que la chaîne "trailing" (`propFirmEngine.ts:333`), que rien n'écrit jamais,
 * tandis que le référentiel écrit 'eod' et 'intraday'. La comparaison échouait
 * donc en silence, le montant partait en perte totale STATIQUE, et le plancher
 * se figeait au solde d'ouverture au lieu de suivre le pic : breach jamais
 * détecté, puis clôture archivée « challenge validé ».
 *
 * 'eod' et 'intraday' sont DEUX TRAILINGS : ils ne diffèrent que par le moment
 * où le pic est réévalué (fin de journée ou en continu), pas par la nature du
 * plancher. 'static' est le seul plancher fixe.
 *
 * Toute valeur inconnue rend `false` — un plancher fixe, l'hypothèse la plus
 * permissive. C'est délibéré : inventer un trailing sur un vocabulaire qu'on ne
 * comprend pas désactiverait des comptes vivants.
 */
export function isTrailingDrawdownType(drawdownType: string | null | undefined): boolean {
  const value = (drawdownType ?? "").trim().toLowerCase();
  return value === "trailing" || value === "eod" || value === "intraday";
}

/**
 * Ce type de drawdown ne reevalue-t-il son pic qu'a la CLOTURE de session ?
 *
 * Le pendant de `isTrailingDrawdownType`, et la SEULE difference entre les deux
 * trailings : 'eod' n'inscrit un nouveau pic qu'au bilan de cloture, la ou
 * 'intraday' le fait des qu'un trade ferme fait monter le solde. Consequence
 * concrete : sur une pointe a 52 000 en seance cloturee a 50 500, l'intraday
 * ancre son plancher sur 52 000 et l'EOD sur 50 500.
 *
 * Volontairement STRICT sur 'eod' : une valeur inconnue rend false, donc retombe
 * sur le pic courant, donc sur le comportement historique. Elargir ce test par
 * defaut ferait basculer des comptes vers une mesure qui n'est pas la leur.
 */
export function isEodDrawdownType(drawdownType: string | null | undefined): boolean {
  return (drawdownType ?? "").trim().toLowerCase() === "eod";
}

/** Un drawdown suiveur est-il déclaré sur ce palier ? */
function declaresTrailing(tier: TierRuleInput): boolean {
  return tier.hasTrailingDrawdown || isTrailingDrawdownType(tier.drawdownType);
}

/**
 * Problèmes de cohérence d'un palier. Tableau vide = palier exploitable.
 *
 * Ne juge PAS la justesse des valeurs (2 000 est-il le bon drawdown ?), mais
 * leur exploitabilité par le moteur : une règle déclarée sans montant est une
 * règle qui ne s'appliquera jamais.
 */
export function validateTierRules(tier: TierRuleInput): TierRuleProblem[] {
  const problems: TierRuleProblem[] = [];

  if (!declaresTrailing(tier)) return problems;

  const hasTrailingValue =
    tier.trailingDrawdownAmount !== null || tier.trailingDrawdownPercent !== null;

  if (!hasTrailingValue) {
    const hasTotalLossValue =
      tier.maxTotalLossAmount !== null || tier.maxTotalLossPercent !== null;

    problems.push({
      field: "trailingDrawdownAmount",
      message: hasTotalLossValue
        // Le cas exact des 8 paliers Lucid : la valeur existe, mais au mauvais
        // endroit. On nomme le champ d'origine pour rendre la correction évidente.
        ? "Drawdown suiveur sans montant : la valeur saisie dans « Max Total Loss » ne sera pas appliquée. Renseignez le montant dans « Trailing Drawdown », ou choisissez le type de drawdown « Perte totale (static) »."
        : "Drawdown suiveur sans montant ni pourcentage : la règle ne sera jamais appliquée.",
    });
  }

  return problems;
}
